import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  SimpleChanges,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'app-infinite-scroll',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #content><ng-content></ng-content></div>
    <div #endMarker style="position: relative;pointer-events: none;" [style.top.px]="-offset()"></div>
  `,
})
export class InfiniteScrollComponent implements OnInit, OnChanges, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  content = viewChild<ElementRef<HTMLDivElement>>('content');
  endMarker = viewChild<ElementRef<HTMLDivElement>>('endMarker');
  resultCount = input.required<number>();
  lastResultId = input.required<string>();
  allResultsLoaded = input<boolean>(false);
  disabled = input<boolean>(false);
  rootMargin = input<string>('500px 0px');
  offset = input<number>(0);
  fetchMore = output();
  private lastFetchId: string;
  private observer: IntersectionObserver;
  private resizeObserver: ResizeObserver;

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    this.observer = new IntersectionObserver(
      entries => {
        if (this.allResultsLoaded() || this.disabled()) return;
        const fetchMore = entries.some(entry => entry.isIntersecting);
        if (fetchMore && this.lastFetchId !== this.lastResultId()) {
          this.lastFetchId = this.lastResultId();
          this.fetchMore.emit();
        }
      },
      { rootMargin: this.rootMargin() }
    );
    this.resizeObserver = new ResizeObserver(() => this.reCheck());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resultCount && this.resultCount) {
      this.reCheck();
    }
    if (changes.disabled && !this.disabled()) {
      this.reCheck();
    }
  }

  ngOnInit() {
    if (isPlatformServer(this.platformId)) return;
    this.observer.observe(this.endMarker().nativeElement);
    this.resizeObserver.observe(this.content().nativeElement);
  }

  private reCheck() {
    const marker = this.endMarker()?.nativeElement;
    if (!marker) return;
    this.observer.unobserve(marker);
    this.observer.observe(marker);
  }

  ngOnDestroy() {
    this.observer.disconnect();
    this.resizeObserver.disconnect();
  }
}
